import clsx from "clsx";
import { Check, ChevronDown, Plus } from "lucide-react";
import {
  Children,
  Fragment,
  isValidElement,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { type Awaiting, awaitingAttr } from "./awaiting";
import { ESCAPE, registerEscape } from "./escapeStack";
import { Motion, Presence } from "./presence";
import { inputClass, SectionLabel } from "./ui";

/** Liste déroulante de la charte web, à la place du `<select>` natif dont la liste, dessinée par le système,
 *  apparaît d'un coup (règle d'Alexis du 29/09/2026 : tout menu apparaît en douceur). S'écrit comme un
 *  `<select>` : enfants `<option>` et `<optgroup>`, `onChange` reçoit `{ target: { value } }`. Liste flottante
 *  au-dessus de tout (portail), sous le champ ou au-dessus faute de place ; clavier : flèches, Début, Fin, Entrée,
 *  Espace, Échap, Tab, et saisie des premières lettres, champ fermé comme ouvert. Entrées alignées sur `MenuItem`
 *  (32 px, coins de 6 px, 12 px), titres de groupe en petites capitales (`SectionLabel`). La liste passe au plan
 *  z-100, au-dessus des fenêtres (z-50) et des confirmations (z-90) qui contiennent des champs.
 *
 *  Option d'action (`<option value="…" data-action="">`) : une action, pas une valeur — son choix appelle
 *  `onAction(value)` au lieu de `onChange`, et la liste garde sa valeur précédente. Elle porte un « + » à la place de
 *  la coche ; les options d'action suivent la liste, sous un filet (hors des groupes).
 *  `data-blocked` sur une option d'action : préalable manquant (R12) — estompée et `aria-disabled`, mais son choix
 *  appelle quand même `onAction`, qui explique ce qui manque. */

export interface Option {
  value: string;
  label: ReactNode;
  text: string;
  group: string | null;
  disabled: boolean;
  action: boolean;
  blocked: boolean;
}

type OptionProps = {
  value?: unknown;
  label?: unknown;
  children?: ReactNode;
  disabled?: boolean;
  "data-action"?: unknown;
  "data-blocked"?: unknown;
};

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement(node)) return textOf((node as ReactElement<{ children?: ReactNode }>).props.children);
  return "";
}

/** Options lues dans les enfants (`option`, `optgroup`) ; exportée pour les tests. */
export function parse(children: ReactNode): Option[] {
  const out: Option[] = [];
  const visit = (nodes: ReactNode, group: string | null) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      const el = child as ReactElement<OptionProps>;
      if (el.type === Fragment) visit(el.props.children, group);
      else if (el.type === "optgroup") visit(el.props.children, String(el.props.label ?? ""));
      else if (el.type === "option") {
        const text = textOf(el.props.children);
        out.push({
          value: el.props.value === undefined ? text : String(el.props.value),
          label: el.props.children,
          text,
          group,
          disabled: Boolean(el.props.disabled),
          action: el.props["data-action"] !== undefined,
          blocked: el.props["data-action"] !== undefined && el.props["data-blocked"] !== undefined,
        });
      }
    });
  };
  visit(children, null);
  return out;
}

const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

interface Placement {
  left: number;
  top: number;
  minWidth: number;
  maxHeight: number;
  above: boolean;
}

const MAX_LIST = 320;

export function Select({
  value,
  onChange,
  children,
  className,
  disabled = false,
  placeholder = "—",
  title,
  "aria-label": ariaLabel,
  onAction,
  awaiting,
}: {
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  /** Choix d'une option d'action (`data-action`) : la valeur reste celle d'avant. */
  onAction?: (value: string) => void;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  title?: string;
  "aria-label"?: string;
  /** Champ à renseigner (`components/awaiting.ts`) : surbrillance du bouton tant que le choix manque. */
  awaiting?: Awaiting;
}) {
  const options = useMemo(() => parse(children), [children]);
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const typed = useRef({ buffer: "", at: 0 });
  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = options[selectedIndex];
  const enabled = (i: number) => Boolean(options[i]) && !options[i]!.disabled;

  const place = useCallback(() => {
    const r = trigger.current?.getBoundingClientRect();
    if (!r) return;
    const margin = 8;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    const wanted = Math.min(MAX_LIST, options.length * 32 + 12);
    const above = spaceBelow < wanted && spaceAbove > spaceBelow;
    const maxHeight = Math.max(96, Math.min(MAX_LIST, (above ? spaceAbove : spaceBelow) - 4));
    const minWidth = r.width;
    const left = Math.max(margin, Math.min(r.left, window.innerWidth - margin - Math.max(minWidth, 200)));
    setPlacement({ left, top: above ? r.top - 4 : r.bottom + 4, minWidth, maxHeight, above });
  }, [options.length]);

  const openList = () => {
    if (disabled) return;
    place();
    setActive(selectedIndex >= 0 ? selectedIndex : options.findIndex((o) => !o.disabled));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  };
  const choose = (i: number) => {
    if (!enabled(i)) return;
    const option = options[i]!;
    close();
    if (option.action) {
      onAction?.(option.value);
      return;
    }
    if (option.value !== value) onChange({ target: { value: option.value } });
  };

  // Échap quand le champ n'a plus le focus (la liste ouverte le garde d'ordinaire, et son clavier traite alors
  // Échap lui-même) : la pile partagée ferme la liste, et seulement elle.
  useEffect(() => {
    if (!open) return;
    return registerEscape(ESCAPE.menu, () => {
      setOpen(false);
    });
  }, [open]);

  // Clic ailleurs : fermeture ; défilement d'un panneau : la liste suit son champ ; fenêtre redimensionnée : fermeture.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (trigger.current?.contains(target) || list.current?.contains(target)) return;
      setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (list.current?.contains(e.target as Node)) return;
      place();
    };
    const onResize = () => setOpen(false);
    document.addEventListener("pointerdown", onDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, place]);

  useLayoutEffect(() => {
    if (open) list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const step = (from: number, dir: 1 | -1) => {
    for (let k = 1; k <= options.length; k++) {
      const i = (from + dir * k + options.length) % options.length;
      if (enabled(i)) return i;
    }
    return from;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
      case "ArrowUp":
        e.preventDefault();
        if (!open) openList();
        else setActive((a) => step(a < 0 ? (e.key === "ArrowDown" ? -1 : 0) : a, e.key === "ArrowDown" ? 1 : -1));
        return;
      case "Home":
      case "End":
        if (!open) return;
        e.preventDefault();
        setActive(e.key === "Home" ? step(-1, 1) : step(0, -1));
        return;
      case "Enter":
      case " ":
        e.preventDefault();
        if (!open) openList();
        else choose(active);
        return;
      case "Escape":
        if (!open) return;
        e.preventDefault();
        e.stopPropagation(); // la fenêtre ou le panneau qui contient le champ reste ouvert
        close();
        return;
      case "Tab":
        if (open) setOpen(false);
        return;
    }
    if (e.key.length !== 1 || e.metaKey || e.ctrlKey || e.altKey) return;
    // Saisie des premières lettres : option suivante qui commence par le texte tapé (0,6 s entre deux touches).
    const now = Date.now();
    const t = typed.current;
    t.buffer = now - t.at > 600 ? e.key : t.buffer + e.key;
    t.at = now;
    const needle = fold(t.buffer);
    const from = open ? active : selectedIndex;
    const start = t.buffer.length > 1 ? Math.max(from, 0) - 1 : from;
    for (let k = 1; k <= options.length; k++) {
      const i = (start + k + options.length) % options.length;
      if (enabled(i) && !options[i]!.action && fold(options[i]!.text).startsWith(needle)) {
        e.preventDefault();
        if (open) setActive(i);
        else if (options[i]!.value !== value) onChange({ target: { value: options[i]!.value } });
        return;
      }
    }
  };

  let lastGroup: string | null = null;
  return (
    <>
      <button
        ref={trigger}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id}
        aria-activedescendant={open && active >= 0 ? `${id}-${active}` : undefined}
        aria-label={ariaLabel}
        {...awaitingAttr(awaiting)}
        title={title ?? (selected ? selected.text : undefined)}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
        className={clsx(className ?? inputClass, "flex items-center justify-between gap-2 text-left")}
      >
        <span className={clsx("min-w-0 flex-1 truncate", !selected && "text-ink-muted")}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={clsx("shrink-0 text-ink-muted transition-transform duration-(--gx-enter)", open && "rotate-180")}
        />
      </button>
      {createPortal(
        <Presence show={open && placement !== null}>
          {open && placement && (
            <Motion
              ref={list}
              origin={placement.above ? "bottom" : "top"}
              id={id}
              role="listbox"
              aria-label={ariaLabel}
              onPointerDown={(e) => e.preventDefault()} // le champ garde le focus (clavier)
              className="fixed z-[100] overflow-y-auto overscroll-contain rounded-xl border border-line bg-surface p-1 shadow-float"
              style={{
                left: placement.left,
                minWidth: placement.minWidth,
                maxWidth: Math.max(placement.minWidth, 440),
                maxHeight: placement.maxHeight,
                ...(placement.above ? { bottom: window.innerHeight - placement.top } : { top: placement.top }),
              }}
            >
              {options.map((o, i) => {
                const header = o.group !== lastGroup && o.group && !o.action ? o.group : null;
                lastGroup = o.group;
                // Première option d'action après les valeurs : filet de séparation (marges : rien ne le touche).
                const rule = i > 0 && o.action && !options[i - 1]!.action;
                const isSelected = o.value === value;
                return (
                  <div key={`${o.group ?? ""}|${o.value}|${i}`}>
                    {rule && <div role="separator" className="my-1 h-px bg-line" />}
                    {header && (
                      <SectionLabel inset className="px-2 pt-2 pb-1">
                        {header}
                      </SectionLabel>
                    )}
                    <div
                      id={`${id}-${i}`}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={o.disabled || o.blocked || undefined}
                      data-index={i}
                      onPointerEnter={() => !o.disabled && setActive(i)}
                      onClick={() => choose(i)}
                      title={o.text}
                      className={clsx(
                        "flex min-h-8 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-ui text-ink",
                        i === active && "bg-hover",
                        isSelected && "font-medium",
                        o.disabled && "cursor-default opacity-40",
                        o.blocked && "opacity-40",
                      )}
                    >
                      {o.action ? (
                        <Plus size={14} className="shrink-0 text-ink-muted" />
                      ) : (
                        <Check size={14} className={clsx("shrink-0", isSelected ? "text-accent" : "invisible")} />
                      )}
                      <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    </div>
                  </div>
                );
              })}
            </Motion>
          )}
        </Presence>,
        document.body,
      )}
    </>
  );
}
